export type AuthIssueKind = "exists" | "invalid" | "expired_link" | "other";

export const EXPIRED_LINK_MESSAGE =
    "This reset link has expired or has already been used. Request a new one.";

// Turns an error from Supabase Auth into text that is safe to show to people.
// Raw Supabase messages (for example "Auth session missing!") are never shown.
export function classifyAuthError(err: unknown): {
    kind: AuthIssueKind;
    text: string;
} {
    const raw = err as { message?: string; code?: string } | string | null;
    const message = (typeof raw === "string" ? raw : raw?.message || "").toLowerCase();
    const code = (typeof raw === "string" ? "" : raw?.code || "").toLowerCase();

    if (
        code === "user_already_exists" ||
        code === "email_exists" ||
        message.includes("already registered") ||
        message.includes("already been registered")
    ) {
        return { kind: "exists", text: "An account with this email already exists." };
    }
    if (code === "invalid_credentials" || message.includes("invalid login credentials")) {
        return { kind: "invalid", text: "Email or password is incorrect." };
    }
    if (code === "email_not_confirmed" || message.includes("email not confirmed")) {
        return {
            kind: "other",
            text: "Please confirm your email address before signing in.",
        };
    }
    if (code === "weak_password" || message.includes("password should be")) {
        return { kind: "other", text: "Password must be at least 6 characters." };
    }
    if (code === "same_password" || message.includes("different from the old password")) {
        return {
            kind: "other",
            text: "Your new password must be different from your current one.",
        };
    }
    if (
        code === "over_request_rate_limit" ||
        code === "over_email_send_rate_limit" ||
        message.includes("rate limit") ||
        message.includes("security purposes")
    ) {
        return {
            kind: "other",
            text: "Too many attempts. Please wait a few minutes and try again.",
        };
    }
    if (
        code === "otp_expired" ||
        code === "session_not_found" ||
        message.includes("auth session missing") ||
        message.includes("has expired") ||
        message.includes("invalid or has expired")
    ) {
        return { kind: "expired_link", text: EXPIRED_LINK_MESSAGE };
    }
    return { kind: "other", text: "Something went wrong. Please try again." };
}
